import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import BlockIcon from "@material-ui/icons/Block";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ScheduleBlockModal from "../../components/ScheduleBlockModal";
import SegmentedControl from "../../components/SegmentedControl";
import { MobileRecordCardSkeleton } from "../../components/MobileRecordCard";
import listPageStyles from "../../styles/listPageStyles";
import {
  groupBlocks,
  formatDayGroup,
  formatDayShort,
  formatDate,
  formatDuration,
  initials,
  avatarColor,
  periodLabel,
} from "../../helpers/scheduleBlocks";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const PAST_DAYS = 90;
const fmt = (m) => m.format("YYYY-MM-DD");

const useStyles = makeStyles((theme) => ({
  ...listPageStyles(theme),
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0, 1.5),
    minHeight: 44,
  },
  tab: { textTransform: "none", fontWeight: 600, minHeight: 44, minWidth: 0 },
  badge: {
    marginLeft: theme.spacing(0.75),
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: "1px 7px",
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
    fontVariantNumeric: "tabular-nums",
  },
  groupRow: {
    "& > td": {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  who: { display: "flex", alignItems: "center", gap: theme.spacing(1.25) },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    fontSize: 12,
    fontWeight: 700,
    color: "#fff",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 24,
    padding: theme.spacing(0, 1.25),
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
    "& svg": { fontSize: 14 },
  },
  chipFull: {
    backgroundColor: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.type === "dark" ? "#EF7B7B" : theme.palette.error.dark,
  },
  chipTime: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
  },
  dur: {
    marginLeft: theme.spacing(0.75),
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  pastRow: { "& > td": { opacity: 0.7 } },
  filters: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: theme.spacing(1.5), flex: 1 },
  select: {
    minWidth: 200,
    "& .MuiOutlinedInput-root": { borderRadius: 10 },
    [theme.breakpoints.down("xs")]: { minWidth: 0, flex: "1 1 100%" },
  },
  dateField: { width: 150, "& .MuiOutlinedInput-root": { borderRadius: 10 } },
  mobileList: { paddingBottom: theme.spacing(1) },
  mobileFilters: {
    display: "flex",
    gap: theme.spacing(0.75),
    overflowX: "auto",
    padding: theme.spacing(1.5, 1.5, 1),
  },
  mobileGroup: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    padding: theme.spacing(1.25, 2, 0.75),
  },
  mobileCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    margin: theme.spacing(0, 1.25, 1),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    cursor: "pointer",
    "&:active": { backgroundColor: theme.palette.action.hover },
  },
  mobileBody: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: theme.spacing(0.75) },
  mobileName: { fontWeight: 600 },
  mobileReason: { color: theme.palette.text.secondary, fontSize: 13, wordBreak: "break-word" },
  filterChip: {
    flex: "none",
    height: 32,
    padding: theme.spacing(0, 1.5),
    borderRadius: 16,
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    color: theme.palette.text.primary,
    font: "inherit",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  },
  filterChipOn: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    borderColor: "transparent",
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

const PRESETS = [
  { value: "7", label: "7 dias" },
  { value: "30", label: "30 dias" },
  { value: "month", label: "Este mês" },
  { value: "custom", label: "Personalizado" },
];

const ScheduleBlocks = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useContext(AuthContext);
  const isAdmin = user.profile === "admin";

  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [preset, setPreset] = useState("30");
  const [customFrom, setCustomFrom] = useState(fmt(moment()));
  const [customTo, setCustomTo] = useState(fmt(moment().add(30, "days")));
  const [providers, setProviders] = useState([]);
  const [providerFilter, setProviderFilter] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);

  // Profissionais para o filtro (só admin vê o filtro)
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/users/list");
        setProviders((data || []).filter((u) => u.isServiceProvider));
      } catch (err) {
        toastError(err);
      }
    })();
  }, [isAdmin]);

  const range = useMemo(() => {
    const now = moment();
    if (preset === "7") return [fmt(now), fmt(now.clone().add(7, "days"))];
    if (preset === "month") return [fmt(now), fmt(now.clone().endOf("month"))];
    if (preset === "custom") return [customFrom, customTo];
    return [fmt(now), fmt(now.clone().add(30, "days"))];
  }, [preset, customFrom, customTo]);

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    const userId = providerFilter || undefined;
    try {
      const [up, pa] = await Promise.all([
        api.get("/schedule-blocks", { params: { userId, dateFrom: range[0], dateTo: range[1] } }),
        api.get("/schedule-blocks", {
          params: {
            userId,
            dateFrom: fmt(moment().subtract(PAST_DAYS, "days")),
            dateTo: fmt(moment().subtract(1, "day")),
          },
        }),
      ]);
      setUpcoming(up.data.blocks || []);
      setPast(pa.data.blocks || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [providerFilter, range]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const matches = useCallback(
    (item) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        (item.reason || "").toLowerCase().includes(q) ||
        (item.user?.name || "").toLowerCase().includes(q)
      );
    },
    [search]
  );

  const upcomingItems = useMemo(() => groupBlocks(upcoming).filter(matches), [upcoming, matches]);
  const pastItems = useMemo(
    () => groupBlocks(past).filter(matches).reverse(),
    [past, matches]
  );
  const items = tab === "upcoming" ? upcomingItems : pastItems;
  const isPast = tab === "past";

  // Agrupa por dia de início (mais recente primeiro no histórico)
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      if (!map.has(it.dateStart)) map.set(it.dateStart, []);
      map.get(it.dateStart).push(it);
    });
    return Array.from(map.entries()).map(([date, list]) => ({ date, list }));
  }, [items]);

  const groupLabel = (g) => {
    const multi = g.list.find((it) => it.days > 1);
    return g.list.length === 1 && multi
      ? `${formatDayShort(multi.dateStart)} → ${formatDayShort(multi.dateEnd)}`
      : formatDayGroup(g.date);
  };

  const openNew = () => {
    setEditingItem(null);
    setModalOpen(true);
  };
  const openEdit = (item) => {
    setEditingItem(item);
    setModalOpen(true);
  };
  const askDelete = (item) => {
    setModalOpen(false);
    setDeletingItem(item);
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    try {
      await Promise.all(deletingItem.ids.map((id) => api.delete(`/schedule-blocks/${id}`)));
      toast.success(deletingItem.days > 1 ? "Bloqueios removidos." : "Bloqueio removido.");
      fetchBlocks();
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingItem(null);
    }
  };

  const countLabel = (() => {
    if (loading && !upcoming.length && !past.length) return "";
    const n = upcomingItems.length;
    const desc =
      preset === "7"
        ? "nos próximos 7 dias"
        : preset === "month"
        ? "até o fim do mês"
        : preset === "custom"
        ? `de ${formatDate(range[0])} a ${formatDate(range[1])}`
        : "nos próximos 30 dias";
    return `${n} ${n === 1 ? "bloqueio" : "bloqueios"} ${desc}`;
  })();

  const renderPeriod = (it) =>
    it.isFullDay ? (
      <>
        <span className={`${classes.chip} ${classes.chipFull}`}>
          <BlockIcon /> Dia inteiro
        </span>
        {it.days > 1 && <span className={classes.dur}>{it.days} dias</span>}
      </>
    ) : (
      <>
        <span className={`${classes.chip} ${classes.chipTime}`}>
          <AccessTimeIcon /> {periodLabel(it)}
        </span>
        <span className={classes.dur}>
          {formatDuration(it.startTime, it.endTime)}
          {it.days > 1 ? ` · ${it.days} dias` : ""}
        </span>
      </>
    );

  const renderActions = (it) => (
    <div className={classes.actions}>
      <Tooltip title="Editar">
        <IconButton size="small" className={classes.iconAction} aria-label="Editar" onClick={() => openEdit(it)}>
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Excluir">
        <IconButton
          size="small"
          className={classes.deleteAction}
          aria-label="Excluir"
          onClick={() => setDeletingItem(it)}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );

  const renderAvatar = (it) => (
    <span className={classes.avatar} style={{ backgroundColor: avatarColor(it.userId) }}>
      {initials(it.user?.name)}
    </span>
  );

  const emptyState = (
    <Box className={classes.emptyState}>
      <EventBusyIcon className={classes.emptyIcon} />
      <Typography variant="body2">
        {search
          ? `Nenhum bloqueio encontrado para "${search}"`
          : isPast
          ? `Nenhum bloqueio nos últimos ${PAST_DAYS} dias.`
          : "Nenhum bloqueio neste período."}
      </Typography>
      {!search && !isPast && (
        <Typography variant="body2" className={classes.muted}>
          Os profissionais estão com a agenda livre.
        </Typography>
      )}
      {!search && !isPast && (
        <Button
          variant="outlined"
          className={`${classes.headerButton} ${classes.secondaryButton}`}
          startIcon={<AddIcon />}
          onClick={openNew}
        >
          Criar bloqueio
        </Button>
      )}
    </Box>
  );

  const profileSelect = isAdmin && (
    <TextField
      select
      className={classes.select}
      variant="outlined"
      size="small"
      value={providerFilter}
      onChange={(e) => setProviderFilter(e.target.value)}
      SelectProps={{ displayEmpty: true }}
      inputProps={{ "aria-label": "Filtrar por profissional" }}
    >
      <MenuItem value="">Todos os profissionais</MenuItem>
      {providers.map((p) => (
        <MenuItem key={p.id} value={p.id}>
          {p.name}
        </MenuItem>
      ))}
    </TextField>
  );

  const customDates = preset === "custom" && !isPast && (
    <>
      <TextField
        className={classes.dateField}
        label="De"
        type="date"
        size="small"
        variant="outlined"
        value={customFrom}
        onChange={(e) => setCustomFrom(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        className={classes.dateField}
        label="Até"
        type="date"
        size="small"
        variant="outlined"
        value={customTo}
        onChange={(e) => setCustomTo(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    </>
  );

  const deleteTitle = deletingItem
    ? deletingItem.days > 1
      ? `Excluir ${deletingItem.days} dias de bloqueio?`
      : "Excluir bloqueio?"
    : "";

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleteTitle}
        open={Boolean(deletingItem)}
        onClose={() => setDeletingItem(null)}
        onConfirm={handleDelete}
      >
        {deletingItem && (
          <>
            <b>{deletingItem.user?.name || ""}</b> ·{" "}
            {deletingItem.days > 1
              ? `${formatDayShort(deletingItem.dateStart)} → ${formatDayShort(deletingItem.dateEnd)}`
              : formatDate(deletingItem.dateStart)}{" "}
            · {periodLabel(deletingItem).toLowerCase()}
            {deletingItem.reason ? ` · ${deletingItem.reason}` : ""}
            <br />
            Os horários voltam a ficar disponíveis para agendamento.
          </>
        )}
      </ConfirmationModal>

      <ScheduleBlockModal
        open={modalOpen}
        item={editingItem}
        onClose={() => setModalOpen(false)}
        onSaved={fetchBlocks}
        onDelete={askDelete}
      />

      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <Title>Bloqueios de Agenda</Title>
          {countLabel && (
            <Typography variant="body2" className={classes.muted}>
              {countLabel}
            </Typography>
          )}
        </div>
        <div className={classes.headerButtons}>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            className={classes.headerButton}
            startIcon={<AddIcon />}
            onClick={openNew}
          >
            Novo bloqueio
          </Button>
        </div>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.toolbar}>
          <TextField
            className={classes.searchField}
            placeholder="Buscar por motivo ou profissional"
            type="search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            inputProps={{ "aria-label": "Buscar bloqueios" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
          {!isMobile && (
            <div className={classes.filters}>
              {profileSelect}
              {!isPast && (
                <SegmentedControl
                  ariaLabel="Período"
                  value={preset}
                  onChange={setPreset}
                  options={PRESETS}
                />
              )}
              {customDates}
            </div>
          )}
        </div>

        {isMobile && (
          <div className={classes.mobileFilters}>
            {!isPast &&
              PRESETS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  className={`${classes.filterChip} ${preset === p.value ? classes.filterChipOn : ""}`}
                  onClick={() => setPreset(p.value)}
                >
                  {p.label}
                </button>
              ))}
          </div>
        )}
        {isMobile && (profileSelect || customDates) && (
          <div className={classes.toolbar} style={{ borderBottom: 0, paddingTop: 0 }}>
            {profileSelect}
            {customDates}
          </div>
        )}

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            className={classes.tab}
            value="upcoming"
            label={
              <>
                Próximos <span className={classes.badge}>{upcomingItems.length}</span>
              </>
            }
          />
          <Tab
            className={classes.tab}
            value="past"
            label={
              <>
                Passados <span className={classes.badge}>{pastItems.length}</span>
              </>
            }
          />
        </Tabs>

        <div className={classes.tableWrapper}>
          {!loading && items.length === 0 && emptyState}

          {isMobile ? (
            (items.length > 0 || loading) && (
              <div className={classes.mobileList}>
                {groups.map((g) => (
                  <React.Fragment key={g.date}>
                    <div className={classes.mobileGroup}>{groupLabel(g)}</div>
                    {g.list.map((it) => (
                      <div
                        key={it.key}
                        className={classes.mobileCard}
                        role="button"
                        tabIndex={0}
                        onClick={() => openEdit(it)}
                        onKeyDown={(e) => e.key === "Enter" && openEdit(it)}
                        style={isPast ? { opacity: 0.75 } : undefined}
                      >
                        {renderAvatar(it)}
                        <div className={classes.mobileBody}>
                          <span className={classes.mobileName}>{it.user?.name || "—"}</span>
                          <span>{renderPeriod(it)}</span>
                          {it.reason && <span className={classes.mobileReason}>{it.reason}</span>}
                        </div>
                        <EditOutlinedIcon fontSize="small" className={classes.muted} />
                      </div>
                    ))}
                  </React.Fragment>
                ))}
                {loading && [0, 1, 2].map((i) => <MobileRecordCardSkeleton key={i} />)}
              </div>
            )
          ) : (
            (items.length > 0 || loading) && (
              <Table stickyHeader size="small" style={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} style={{ paddingLeft: 20 }}>
                      Profissional
                    </TableCell>
                    <TableCell className={classes.headCell}>Período</TableCell>
                    <TableCell className={classes.headCell}>Motivo</TableCell>
                    <TableCell className={classes.headCell} align="right" style={{ paddingRight: 20, width: 110 }}>
                      Ações
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((g) => (
                    <React.Fragment key={g.date}>
                      <TableRow className={classes.groupRow}>
                        <TableCell colSpan={4} style={{ paddingLeft: 20 }}>
                          {groupLabel(g)}
                        </TableCell>
                      </TableRow>
                      {g.list.map((it) => (
                        <TableRow key={it.key} className={`${classes.row} ${isPast ? classes.pastRow : ""}`}>
                          <TableCell style={{ paddingLeft: 20 }}>
                            <div className={classes.who}>
                              {renderAvatar(it)}
                              {it.user?.name || "—"}
                            </div>
                          </TableCell>
                          <TableCell>{renderPeriod(it)}</TableCell>
                          <TableCell>
                            {it.reason || <span className={classes.muted}>Sem motivo</span>}
                          </TableCell>
                          <TableCell style={{ paddingRight: 20 }}>{renderActions(it)}</TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  ))}
                  {loading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            )
          )}
        </div>

        {items.length > 0 && (
          <div className={classes.footer}>
            {isPast
              ? `Mostrando ${items.length} · últimos ${PAST_DAYS} dias`
              : `Mostrando ${items.length} · ${formatDate(range[0])} a ${formatDate(range[1])}`}
          </div>
        )}
      </Paper>
    </MainContainer>
  );
};

export default ScheduleBlocks;
